import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPath } from '../../../pages/Pages';
import { useSidebarState } from './SidebarStateContext';
import type { Menu, Submenu } from './types';

export type NavigableEntry = Pick<
    Submenu,
    'url' | 'path' | 'params' | 'external'
>;

/** Ruta a la que apunta un menú, para poder pintarla en el href del enlace. */
export function hrefFor(entry: NavigableEntry): string | undefined {
    if (entry.url) return entry.url;
    if (entry.path) return getPath(entry.path as any, entry.params);
    return undefined;
}

/**
 * Navegación de los menús del sidebar. Mantiene el comportamiento anterior:
 * los enlaces externos fuerzan recarga completa (para que el proxy resuelva la
 * redirección) y el resto navega con el router. Al navegar se cierra el
 * sidebar móvil.
 */
export function useMenuNavigation() {
    const navigate = useNavigate();
    const location = useLocation();
    const { closeMobile } = useSidebarState();

    const go = useCallback(
        (entry: NavigableEntry): void => {
            // Enlaces externos: recarga completa para que el proxy gestione la
            // redirección.
            if (entry.external && entry.path) {
                window.location.href = getPath(entry.path as any, entry.params);
                return;
            }

            const href = hrefFor(entry);
            if (!href) return;

            navigate(href);
            closeMobile();
        },
        [navigate, closeMobile],
    );

    const isActive = useCallback(
        (entry: NavigableEntry): boolean => {
            const href = hrefFor(entry);
            return href !== undefined && location.pathname === href;
        },
        [location.pathname],
    );

    const isMenuActive = useCallback(
        (menu: Menu): boolean =>
            isActive(menu) ||
            (menu.submenus ?? []).some((submenu) => isActive(submenu)),
        [isActive],
    );

    return { go, isActive, isMenuActive };
}
